import { Component, OnInit, ElementRef, ViewChild, forwardRef, Input, SimpleChanges, OnChanges, HostListener, HostBinding, Output, EventEmitter, Inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil, set } from 'lodash-es';
import { Styles } from './models/style';
import { SelectorComponent } from './components/selector.component';
import { ESelectorType } from './components/selector.component';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { PurchaseOrders_ShellService } from './PurchaseOrders.shell.service';
import { PurchaseOrders_OperationService } from './PurchaseOrders.operation.service';
import { PurchaseOrders_DatasourceService } from './PurchaseOrders.datasource.index';
import { PurchaseOrders_FlowService } from './PurchaseOrders.flow.index';
import { PurchaseOrders_ReportService } from './PurchaseOrders.report.index';
import { PurchaseOrders_LocalizationService } from './PurchaseOrders.localization.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './PurchaseOrders.frontend.types'
import { $frontendTypes as $types} from './PurchaseOrders.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';


@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'PurchaseOrders-reasoncodes_dd_multi',
  templateUrl: './PurchaseOrders.reasoncodes_dd_multi.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PurchaseOrders_reasoncodes_dd_multiComponent),
      multi: true
    }
  ]
})
export class PurchaseOrders_reasoncodes_dd_multiComponent extends BaseComponent implements OnInit, ControlValueAccessor, OnChanges {

  @Input() type?: ESelectorType;
  _disabled = false;
  @Input() get disabled() { 
      return this._disabled 
      || this.$hasMissingRequiredInParams
      ; }
  set disabled(val: any) { this._disabled = val; }

  @Input() placeholder = '';
  @Input() styles: Styles;
  @Input() tooltip = '';

  @Output() displayTextChange: EventEmitter<string> = new EventEmitter();

  @ViewChild(SelectorComponent) selector: SelectorComponent;

  @HostBinding('tabIndex') get tabIndex() { return -1; }
  @HostListener('focus')
  focus() {
    this.selector?.focus();
  }

  get isMulti() {
    return true;
  }

  get isSingle() {
    return false;
  }


  inParams: { parentId: number, parentEntity: string } = { parentId: null, parentEntity: null };
  @Input('parentId') set $inParams_parentId(value: any) {
    this.inParams['parentId'] = value;
  }
  get $inParams_parentId(): any {
    return this.inParams['parentId'] ;
  }
  @Input('parentEntity') set $inParams_parentEntity(value: any) {
    this.inParams['parentEntity'] = value;
  }
  get $inParams_parentEntity(): any {
    return this.inParams['parentEntity'] ;
  }

  fullTextSearch: string;

  constructor(
    private utils: UtilsService,
    private settings: SettingsValuesService,
    private shell: PurchaseOrders_ShellService,
    private datasources: PurchaseOrders_DatasourceService,
    private flows: PurchaseOrders_FlowService,
    private reports: PurchaseOrders_ReportService,
    private localization: PurchaseOrders_LocalizationService,
    private operations: PurchaseOrders_OperationService,
    private logger: CleanupLoggerService,
    private _elementRef: ElementRef
    ) {
      super();
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.parentId)) {
        this.$missingRequiredInParams.push('parentId');
      }
      if(isNil(this.inParams.parentEntity)) {
        this.$missingRequiredInParams.push('parentEntity');
      }
  }

  async $init() {
    this.selector?.init();
  }

  onKeyDown($event: KeyboardEvent) {
    //TODO:KeyDown    
  }

  //ControlValueAccessor
  _innerValue: any;

  onChange: any = () => { };
  onTouch: any = () => { };

  get value(): number | number[] {
    return this._innerValue;
  }

  set value(val:  number | number[]) {
    if (this._innerValue !== val) {
      this._innerValue = val;
      this.onChange(val);
      this.onTouch(val);
    }
  }

  writeValue(value: any): void {
    this._innerValue = value;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  displayWithFn(value: any): Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}> {
    const promise = new Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}>((resolve, reject) => {

      const $selector = this;
      const $utils = this.utils;

      this.fullTextSearch = null;
      const $fullTextSearch = null;
      this.datasources.PurchaseOrders.ds_reasoncodes_dd.getByKeys({
        $keys: value,
        parentId:  $selector.inParams.parentId ,
        parentEntity:  $selector.inParams.parentEntity ,
        fullTextSearch:  $selector.fullTextSearch ,
      }).then(result => {
        const selected = value.map(k => {
          const found = result.result
            .find(i => i.Id === k);
          const e: { Id?: number, LookupCode?: string } = found 
            ?? { 
              Id : k
            };

          const $option = { entity: e };
          const $entity = e;
          const $resultOption = { key: null, name: found ? $option.entity.LookupCode : 'No data to display', disabled: false, selected:true };
          $resultOption.key = $option.entity.Id;
          return $resultOption;
        });
        const displayText = value.map(k => {
          const found = result.result
            .find(i => i.Id === k);
          if (found) {
            const $option = { entity: found };
            const $entity = found;
            return $option.entity.LookupCode;
          } else {
            return 'No data to display';
          }
        })
        .join(
           ', '
        );
        this.displayTextChange.emit(displayText);
        resolve({displayText, selected});
      });
    });
    return promise;
  }

  optionsFn(filterText: string): Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }> {

    const promise = new Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }>((resolve, reject) => {

      const $selector = this;
      const $utils = this.utils;

      this.fullTextSearch = filterText;
      const $fullTextSearch = filterText;
      this.datasources.PurchaseOrders.ds_reasoncodes_dd.getList({
        $top: 15,
        $skip: 0,
        parentId:  $selector.inParams.parentId ,
        parentEntity:  $selector.inParams.parentEntity ,
        fullTextSearch:  $selector.fullTextSearch ,
      }).then(result => {
        resolve({
          list: result.result.map(item => {
            const $option = { entity: item };
            const $entity = item;
            const $resultOption = { key: null, name: $option.entity.LookupCode, disabled: null };
            $resultOption.key = $option.entity.Id;
            return $resultOption;
          }),
          totalCount: result.totalCount,
          top: 15
        });
      });

    });

    return promise;

  }

  //#region private flows
  on_add_action(event = null) {
    return this.on_add_actionInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_add_actionInternal(
    $selector: PurchaseOrders_reasoncodes_dd_multiComponent,
  
    $shell: PurchaseOrders_ShellService,
    $datasources: PurchaseOrders_DatasourceService,
    $flows: PurchaseOrders_FlowService,
    $reports: PurchaseOrders_ReportService,
    $settings: SettingsValuesService,
    $operations: PurchaseOrders_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: PurchaseOrders_LocalizationService,
    $event: any
  ) {
    this.logger.log('PurchaseOrders', 'reasoncodes_dd.on_add_action');
  const newReasonCode = $selector.fullTextSearch;
  
  if ($utils.isDefined(newReasonCode)) {
      // Check for  duplicates
      const reasoncodes = (await $datasources.PurchaseOrders.ds_get_reasoncodes_by_lookupcode_parentId_parentEntityId.get({
          lookupcode: newReasonCode,
          parentId: $selector.inParams.parentId,
          parentEntity: $selector.inParams.parentEntity
      })).result;
      if ($utils.isDefined(reasoncodes)) {
  
          throw new Error('Reason code already exists.')
      }
      else {
          try {
              $selector.value = (await $flows.PurchaseOrders.create_reasoncode_flow({
                  properties: {
                      "LookupCode": newReasonCode,
                      "EnumName": newReasonCode,
                      "Name": newReasonCode,
                      "Label": newReasonCode,
                      "SystemDefined": false,
                      "ParentId": $selector.inParams.parentId,
                      "ParentEntity": $selector.inParams.parentEntity
                  }
              })).reasonCodeId
          }
          catch (error) {
  
              $shell.PurchaseOrders.showErrorDetails('Save', 'Error on save.', error);
              throw error; // to prevent displayMode 
          }
  
  
      }
  
  
  }
  
  else {
      throw new Error('To create a new reason code please first type in the desired value in the drop down then try again.')
  }
  }
  //#endregion private flows
}
